import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import './loginPage.css';

const floatingApps = [
  { name: 'Projet', icon: '/assets/odoo-apps/project.png', position: 'app-project', delay: 0 },
  { name: 'CRM', icon: '/assets/odoo-apps/crm.png', position: 'app-crm', delay: 0 },
  { name: 'Discussion', icon: '/assets/odoo-apps/discuss.png', position: 'app-chat', delay: -1.4 },
  { name: 'Calendrier', icon: '/assets/odoo-apps/calendar.png', position: 'app-calendar', delay: -1.4 },
  { name: 'Feuilles de temps', icon: '/assets/odoo-apps/timesheets.png', position: 'app-timesheet', delay: -2.8 },
  { name: 'Facturation', icon: '/assets/odoo-apps/invoicing.png', position: 'app-invoice', delay: -2.8 },
  { name: 'Employés', icon: '/assets/odoo-apps/employees.png', position: 'app-team', delay: -4.2 },
  { name: 'Tableaux de bord', icon: '/assets/odoo-apps/dashboards.png', position: 'app-analytics', delay: -4.2 },
];

const FloatingApp = ({ app }) => {
  const { icon, name, position, delay } = app;

  return (
    <div
      className={`login-floating-app ${position}`}
      style={{ '--app-delay': `${delay}s` }}
      aria-hidden="true"
    >
      <div className="login-floating-app__content">
        <img className="login-floating-app__icon" src={icon} alt="" />
        <span>{name}</span>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loading } = useAuth();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!formData.username || !formData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      setIsSubmitting(true);
      await login(formData.username, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-page__glow login-page__glow--purple" aria-hidden="true" />
      <div className="login-page__glow login-page__glow--green" aria-hidden="true" />

      <div className="login-apps" aria-hidden="true">
        <div className="login-apps__orbit login-apps__orbit--outer" />
        <div className="login-apps__orbit login-apps__orbit--inner" />
        <div className="login-apps__rotator">
          {floatingApps.map((app) => (
            <FloatingApp key={app.name} app={app} />
          ))}
        </div>
      </div>

      <section className="login-panel" aria-labelledby="login-title">
        <img className="login-panel__logo" src="/assets/logo.png" alt="Dynamic Horizon" />

        <div className="login-panel__intro">
          <h1 id="login-title">Suivi de projet</h1>
          <p>Connectez-vous avec votre compte Odoo pour suivre l’avancement de votre projet.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="login-error" role="alert">
              <AlertCircle size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div className="login-fields">
            <div className="login-field">
              <label htmlFor="username">Identifiant Odoo</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="nom@entreprise.com"
                autoComplete="username"
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="login-field login-field--password">
              <label htmlFor="password">Mot de passe</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mot de passe"
                autoComplete="current-password"
                disabled={isSubmitting}
                required
              />
              <button
                className="login-password-toggle"
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </div>

          <button className="login-submit" type="submit" disabled={isSubmitting || loading}>
            {isSubmitting ? (
              <>
                <Loader2 className="login-submit__spinner" size={20} />
                <span>Connexion en cours…</span>
              </>
            ) : (
              <>
                <span>Se connecter</span>
                <ArrowRight size={19} />
              </>
            )}
          </button>
        </form>

        <p className="login-support">
          Besoin d’aide ?{' '}
          <a
            href="https://mail.google.com/mail/?view=cm&fs=1&to=support%40dynamichorizon.ma"
            target="_blank"
            rel="noreferrer"
          >
            Contactez le support
          </a>
        </p>
      </section>

      <footer className="login-footer">
        <span>© {new Date().getFullYear()} Dynamic Horizon</span>
        <i aria-hidden="true" />
        <span>Odoo Dashboard v1.0.0</span>
      </footer>
    </main>
  );
};

export default LoginPage;
